import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Alert, Box, Button, IconButton, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import isProd from '~/utils/isProd';

import { AiChatContext } from './AiChatContext';
import AiChatConversationList from './AiChatConversationList';
import AiChatInput from './AiChatInput';
import AiChatMessageList from './AiChatMessageList';
import { isDepartmentSelectionQuestion } from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS, SUGGESTED_DRAFT_ACTIONS, SUGGESTED_QUESTIONS } from './chatModes';

const PANEL_DEFAULT_OFFSET = 16;
const VIEWPORT_EDGE_PADDING = 0;
const DEFAULT_PANEL_WIDTH = 440;
const DEFAULT_PANEL_HEIGHT = 760;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 420;
const AI_CHAT_ALLOWED_USERS = [
  'tammie.russell@luxuryescapes.com',
  'caroline.ball@luxuryescapes.com',
  'rex.dimiri@luxuryescapes.com',
  'melissa.crees@luxuryescapes.com',
  'jayme.davis@luxuryescapes.com',
  'lucia.marshall@luxuryescapes.com',
  'natasha.klein@luxuryescapes.com',
  'michael.didham@luxuryescapes.com',
  'samuel.drown@luxuryescapes.com',
  'william.ritossa@luxuryescapes.com',
  'joshua.paling@luxuryescapes.com',
  'shay.hamama@luxuryescapes.com',
  'gregory.fine@luxuryescapes.com',
  'andy.welch@luxuryescapes.com',
  'eugene.moskvita@luxuryescapes.com',
  'jamie.lane@luxuryescapes.com',
  'kristine.lane@luxuryescapes.com',
  'luke.wang@luxuryescapes.com',
  'riley.laverty@luxuryescapes.com',
  'dean.richards@luxuryescapes.com',
  'sitaraman.padmanabhan@luxuryescapes.com',
  'nicholas.puglia@luxuryescapes.com',
  'koren.sela@luxuryescapes.com',
];

function normalizeAllowlistValue(value: string): string {
  return value.trim().toLowerCase();
}

const AI_CHAT_ALLOWED_USER_SET = new Set(AI_CHAT_ALLOWED_USERS.map(normalizeAllowlistValue).filter(Boolean));
const noop = () => undefined;
const asyncNoop = async (): Promise<void> => Promise.resolve();
const asyncNull = async (): Promise<string | null> => null;

interface PanelPosition {
  x: number;
  y: number;
}

interface PanelSize {
  width: number;
  height: number;
}

interface PanelLayout {
  position: PanelPosition;
  size: PanelSize;
}

type ResizeDirection = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
type ResizeEdges = { left?: boolean; right?: boolean; top?: boolean; bottom?: boolean; cursor: string };

const RESIZE_DIRECTIONS: Record<ResizeDirection, ResizeEdges> = {
  top: { top: true, cursor: 'ns-resize' },
  right: { right: true, cursor: 'ew-resize' },
  bottom: { bottom: true, cursor: 'ns-resize' },
  left: { left: true, cursor: 'ew-resize' },
  'top-left': { top: true, left: true, cursor: 'nwse-resize' },
  'top-right': { top: true, right: true, cursor: 'nesw-resize' },
  'bottom-left': { bottom: true, left: true, cursor: 'nesw-resize' },
  'bottom-right': { bottom: true, right: true, cursor: 'nwse-resize' },
};

const RESIZE_HANDLE_SX: Record<ResizeDirection, Record<string, number | string>> = {
  left: { top: 12, left: 0, bottom: 12, width: 12 },
  right: { top: 12, right: 0, bottom: 12, width: 12 },
  top: { left: 12, right: 12, top: 0, height: 12 },
  bottom: { left: 12, right: 12, bottom: 0, height: 12 },
  'top-left': { left: 0, top: 0, width: 18, height: 18 },
  'top-right': { right: 0, top: 0, width: 18, height: 18 },
  'bottom-left': { left: 0, bottom: 0, width: 18, height: 18 },
  'bottom-right': { right: 0, bottom: 0, width: 18, height: 18 },
};

function getDefaultPanelSize(): PanelSize {
  if (typeof window === 'undefined') {
    return { width: DEFAULT_PANEL_WIDTH, height: DEFAULT_PANEL_HEIGHT };
  }

  return {
    width: Math.min(DEFAULT_PANEL_WIDTH, window.innerWidth),
    height: Math.min(DEFAULT_PANEL_HEIGHT, window.innerHeight),
  };
}

function clampPanelPosition(position: PanelPosition, size: PanelSize): PanelPosition {
  if (typeof window === 'undefined') {
    return position;
  }

  const maxX = Math.max(VIEWPORT_EDGE_PADDING, window.innerWidth - size.width - VIEWPORT_EDGE_PADDING);
  const maxY = Math.max(VIEWPORT_EDGE_PADDING, window.innerHeight - size.height - VIEWPORT_EDGE_PADDING);

  return {
    x: Math.min(Math.max(position.x, VIEWPORT_EDGE_PADDING), maxX),
    y: Math.min(Math.max(position.y, VIEWPORT_EDGE_PADDING), maxY),
  };
}

function clampDimension(nextSize: number, minSize: number, maxSize: number): number {
  const effectiveMinSize = Math.min(minSize, maxSize);
  return Math.min(Math.max(nextSize, effectiveMinSize), maxSize);
}

function clampPanelSize(size: PanelSize, position: PanelPosition): PanelSize {
  if (typeof window === 'undefined') {
    return size;
  }

  const maxWidth = Math.max(window.innerWidth - position.x - VIEWPORT_EDGE_PADDING, 0);
  const maxHeight = Math.max(window.innerHeight - position.y - VIEWPORT_EDGE_PADDING, 0);

  return {
    width: clampDimension(size.width, MIN_PANEL_WIDTH, maxWidth),
    height: clampDimension(size.height, MIN_PANEL_HEIGHT, maxHeight),
  };
}

function getDefaultPanelPosition(size: PanelSize): PanelPosition {
  if (typeof window === 'undefined') {
    return { x: VIEWPORT_EDGE_PADDING, y: VIEWPORT_EDGE_PADDING };
  }

  return clampPanelPosition(
    {
      x: window.innerWidth - size.width - PANEL_DEFAULT_OFFSET,
      y: window.innerHeight - size.height - PANEL_DEFAULT_OFFSET,
    },
    size,
  );
}

function getDefaultPanelLayout(): PanelLayout {
  const size = getDefaultPanelSize();
  return { size, position: getDefaultPanelPosition(size) };
}

export default function AiChatDrawer() {
  const chatContext = useContext(AiChatContext);
  const { user: currentUser } = useCurrentUser();
  const panelLayoutRef = useRef<PanelLayout>(getDefaultPanelLayout());
  const [panelSize, setPanelSize] = useState<PanelSize>(() => panelLayoutRef.current.size);
  const [activeTab, setActiveTab] = useState<'chat' | 'conversations'>('chat');
  const [panelPosition, setPanelPosition] = useState<PanelPosition>(() => panelLayoutRef.current.position);
  const [isDragging, setIsDragging] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);
  const [isConversationRefCopied, setIsConversationRefCopied] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const copyResetTimeoutRef = useRef<number | null>(null);
  const resizeOriginRef = useRef({
    pointerX: 0,
    pointerY: 0,
    panelX: 0,
    panelY: 0,
    width: DEFAULT_PANEL_WIDTH,
    height: DEFAULT_PANEL_HEIGHT,
  });
  const wasOpenRef = useRef(false);
  const hasAiChatAllowlist = AI_CHAT_ALLOWED_USER_SET.size > 0;
  const userEmail = currentUser?.email?.trim().toLowerCase() || '';
  const isAiChatUserAllowed = !isProd() || !hasAiChatAllowlist || AI_CHAT_ALLOWED_USER_SET.has(userEmail);
  const isOpen = chatContext?.isOpen ?? false;
  const threads = chatContext?.threads ?? {};
  const activeThreadId = chatContext?.activeThreadId ?? null;
  const contextActiveThread = chatContext?.activeThread ?? null;
  const sendingThreadIds = chatContext?.sendingThreadIds ?? [];
  const errorsByThreadId = chatContext?.errorsByThreadId ?? {};
  const globalError = chatContext?.globalError ?? null;
  const currentContextSnapshot = chatContext?.currentContextSnapshot ?? null;
  const closeChat = chatContext?.closeChat || noop;
  const sendMessage = chatContext?.sendMessage || asyncNoop;
  const openChat = chatContext?.openChat || asyncNoop;
  const createThread = chatContext?.createThread || asyncNull;
  const switchThread = chatContext?.switchThread || noop;
  const deleteThread = chatContext?.deleteThread || noop;

  const setPanelLayout = useCallback((layout: PanelLayout) => {
    panelLayoutRef.current = layout;
    setPanelPosition(layout.position);
    setPanelSize(layout.size);
  }, []);
  const handleDragStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target instanceof HTMLElement ? e.target : null;
    if (target?.closest('[data-ai-chat-window-action="true"]')) {
      return;
    }

    dragOffsetRef.current = {
      x: e.clientX - panelLayoutRef.current.position.x,
      y: e.clientY - panelLayoutRef.current.position.y,
    };
    setIsDragging(true);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    function handlePointerMove(e: PointerEvent) {
      setPanelLayout({
        position: clampPanelPosition(
          {
            x: e.clientX - dragOffsetRef.current.x,
            y: e.clientY - dragOffsetRef.current.y,
          },
          panelLayoutRef.current.size,
        ),
        size: panelLayoutRef.current.size,
      });
    }

    function handlePointerUp() {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, setPanelLayout]);

  const handleResizeStart = useCallback(
    (direction: ResizeDirection) => (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      resizeOriginRef.current = {
        pointerX: e.clientX,
        pointerY: e.clientY,
        panelX: panelLayoutRef.current.position.x,
        panelY: panelLayoutRef.current.position.y,
        width: panelLayoutRef.current.size.width,
        height: panelLayoutRef.current.size.height,
      };
      setResizeDirection(direction);
      document.body.style.cursor = RESIZE_DIRECTIONS[direction].cursor;
      document.body.style.userSelect = 'none';
    },
    [],
  );

  useEffect(() => {
    if (!resizeDirection) {
      return undefined;
    }

    function handlePointerMove(e: PointerEvent) {
      const { pointerX, pointerY, panelX, panelY, width, height } = resizeOriginRef.current;
      const deltaX = e.clientX - pointerX;
      const deltaY = e.clientY - pointerY;
      const rightEdge = panelX + width;
      const bottomEdge = panelY + height;
      const { left, right, top, bottom } = RESIZE_DIRECTIONS[resizeDirection];

      const nextPosition: PanelPosition = { x: panelX, y: panelY };
      const nextSize: PanelSize = { width, height };

      if (left) {
        const maxX = Math.max(VIEWPORT_EDGE_PADDING, rightEdge - MIN_PANEL_WIDTH);
        nextPosition.x = Math.min(Math.max(panelX + deltaX, VIEWPORT_EDGE_PADDING), maxX);
        nextSize.width = rightEdge - nextPosition.x;
      }

      if (right && typeof window !== 'undefined') {
        nextSize.width = clampDimension(
          width + deltaX,
          MIN_PANEL_WIDTH,
          window.innerWidth - panelX - VIEWPORT_EDGE_PADDING,
        );
      }

      if (top) {
        const maxY = Math.max(VIEWPORT_EDGE_PADDING, bottomEdge - MIN_PANEL_HEIGHT);
        nextPosition.y = Math.min(Math.max(panelY + deltaY, VIEWPORT_EDGE_PADDING), maxY);
        nextSize.height = bottomEdge - nextPosition.y;
      }

      if (bottom && typeof window !== 'undefined') {
        nextSize.height = clampDimension(
          height + deltaY,
          MIN_PANEL_HEIGHT,
          window.innerHeight - panelY - VIEWPORT_EDGE_PADDING,
        );
      }

      setPanelLayout({ position: nextPosition, size: nextSize });
    }

    function handlePointerUp() {
      setResizeDirection(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizeDirection, setPanelLayout]);

  useEffect(() => {
    function syncPanelPosition() {
      const nextPosition = clampPanelPosition(panelLayoutRef.current.position, panelLayoutRef.current.size);
      setPanelLayout({
        position: nextPosition,
        size: clampPanelSize(panelLayoutRef.current.size, nextPosition),
      });
    }

    syncPanelPosition();
    window.addEventListener('resize', syncPanelPosition);

    return () => {
      window.removeEventListener('resize', syncPanelPosition);
    };
  }, [setPanelLayout]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setPanelLayout(getDefaultPanelLayout());
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, setPanelLayout]);
  const activeThread = contextActiveThread?.mode === 'default' ? contextActiveThread : null;
  const isLoading = activeThread ? sendingThreadIds.includes(activeThread.id) : false;
  const error = activeThread ? errorsByThreadId[activeThread.id] ?? null : null;
  const showLauncher = !isOpen && !('Cypress' in window);
  const defaultThreads = Object.fromEntries(Object.entries(threads).filter(([, thread]) => thread.mode === 'default'));

  const messages = activeThread?.messages || [];
  const showSuggestions = messages.length === 0;
  const hasToolCallResults = messages.some((m) => m.toolCalls && m.toolCalls.length > 0);
  const showDraftActions = !showSuggestions && hasToolCallResults;
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const pendingDepartmentSelection =
    !isLoading && lastMessage?.role === 'assistant' && isDepartmentSelectionQuestion(lastMessage.content);

  const handleSelectThread = useCallback(
    (threadId: string) => {
      switchThread(threadId);
      setActiveTab('chat');
    },
    [switchThread],
  );

  const handleNewChat = useCallback(async () => {
    await createThread('default', currentContextSnapshot);
    setActiveTab('chat');
  }, [createThread, currentContextSnapshot]);

  const handleCopyConversationRef = useCallback(async () => {
    if (!activeThread?.conversationRef) {
      return;
    }

    try {
      await navigator.clipboard.writeText(activeThread.conversationRef);
      setIsConversationRefCopied(true);
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
      copyResetTimeoutRef.current = window.setTimeout(() => {
        setIsConversationRefCopied(false);
        copyResetTimeoutRef.current = null;
      }, 2000);
    } catch {
      // Clipboard access may be unavailable.
    }
  }, [activeThread?.conversationRef]);

  useEffect(() => {
    setIsConversationRefCopied(false);
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = null;
    }
  }, [activeThread?.conversationRef]);

  if (!chatContext || !isAiChatUserAllowed) {
    return null;
  }

  return (
    <>
      {showLauncher && (
        <Button
          variant="contained"
          size="small"
          onClick={() => void openChat()}
          aria-label="Open AI chat"
          startIcon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 99999,
            fontSize: 12,
            py: 1,
            px: 0.75,
            minWidth: 0,
            minHeight: 100,
            borderRadius: '8px 0 0 8px',
            boxShadow: 3,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            gap: 0.75,
            '& .MuiButton-startIcon': {
              margin: 0,
            },
          }}
        >
          AI Chat
        </Button>
      )}

      {isOpen && (
        <Paper
          elevation={12}
          role="dialog"
          aria-label="AI chat panel"
          sx={{
            position: 'fixed',
            left: panelPosition.x,
            top: panelPosition.y,
            width: panelSize.width,
            height: panelSize.height,
            maxWidth: '100vw',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            zIndex: 1300,
          }}
        >
          <Box
            onPointerDown={handleDragStart}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              cursor: isDragging ? 'grabbing' : 'grab',
              bgcolor: 'background.paper',
              flexShrink: 0,
              touchAction: 'none',
            }}
          >
            <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {activeThread?.conversationRef ? (
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  noWrap
                  onClick={handleCopyConversationRef}
                  title={`Copy conversation ref: ${activeThread.conversationRef}`}
                  data-ai-chat-window-action="true"
                  sx={{ cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
                >
                  {isConversationRefCopied ? 'Copied' : activeThread.conversationRef}
                </Typography>
              ) : (
                <Box sx={{ height: 20 }} />
              )}
            </Box>

            <Stack direction="row" spacing={0.5}>
              {activeTab === 'chat' && activeThread?.openaiConversationId ? (
                <IconButton
                  size="small"
                  component="a"
                  href={`https://platform.openai.com/logs/${activeThread.openaiConversationId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View conversation in OpenAI"
                  data-ai-chat-window-action="true"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              ) : null}
              <IconButton
                size="small"
                onClick={() => void handleNewChat()}
                title="New chat"
                data-ai-chat-window-action="true"
              >
                <AddCommentOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={closeChat} title="Close" data-ai-chat-window-action="true">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
          >
            <Tab label="Chat" value="chat" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="History" value="conversations" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {activeTab === 'conversations' ? (
              <AiChatConversationList
                threads={defaultThreads}
                activeThreadId={activeThreadId}
                onSelect={handleSelectThread}
                onDelete={deleteThread}
                onNewChat={() => void handleNewChat()}
              />
            ) : activeThread ? (
              <>
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <AiChatMessageList
                    messages={messages}
                    isLoading={isLoading}
                    orderId={activeThread.latestContextSnapshot?.orderId ?? null}
                    department={activeThread.latestContextSnapshot?.department ?? null}
                  />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                    {error}
                  </Alert>
                )}

                <AiChatInput
                  onSend={(content) => void sendMessage(activeThread.id, content, currentContextSnapshot)}
                  isLoading={isLoading}
                  suggestedQuestions={SUGGESTED_QUESTIONS}
                  showSuggestions={showSuggestions}
                  draftActions={SUGGESTED_DRAFT_ACTIONS}
                  showDraftActions={showDraftActions}
                  selectionOptions={pendingDepartmentSelection ? DEPARTMENT_OPTIONS : []}
                  selectionLabel={pendingDepartmentSelection ? 'Choose your department' : null}
                />
              </>
            ) : (
              <>
                {globalError && (
                  <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
                    {globalError}
                  </Alert>
                )}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    Select a conversation from the History tab, or start a new one.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          {Object.entries(RESIZE_HANDLE_SX).map(([direction, handleSx]) => (
            <Box
              key={direction}
              onPointerDown={handleResizeStart(direction as ResizeDirection)}
              data-ai-chat-window-action="true"
              sx={{
                position: 'absolute',
                cursor: RESIZE_DIRECTIONS[direction as ResizeDirection].cursor,
                zIndex: direction.includes('-') ? 2 : 1,
                ...handleSx,
              }}
            />
          ))}
        </Paper>
      )}
    </>
  );
}
